import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    Eye,
    EyeOff,
    LockKeyhole,
    ShieldCheck,
    Mail,
    Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdmin } from '../../context/AdminContext';
import './Login.css';

const Login = () => {
    const { login } = useAdmin();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');

    const updateField = (field) => (event) => {
        setForm(current => ({ ...current, [field]: event.target.value }));
    };

    const updateEmail = event => {
        const entered = event.target.value;
        const normalized = entered.toLowerCase();
        setForm(current => ({ ...current, email: normalized }));
        setEmailError(entered !== normalized ? 'Use lowercase letters in the admin email address.' : '');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const email = form.email.trim();
        if (!email) { setEmailError('Enter your admin email address.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError('Enter a valid admin email address.'); return; }
        setEmailError('');
        setLoading(true);
        try {
            await login(email, form.password);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || 'Login failed!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            className="login-page"
            style={{ '--login-hero': `url(${process.env.PUBLIC_URL}/matrimony-admin-hero.png)` }}
        >
            <section className="login-showcase" aria-label="Gettimelam Matrimony">
                <img
                    className="login-showcase__image"
                    src="/matrimony-admin-hero.png"
                    alt="A joyful newlywed couple in traditional South Indian wedding attire"
                />
                <div className="login-showcase__wash" />

                <div className="login-brand">
                    <img className="login-brand__logo" src="/logo.png" alt="Gettimelam Matrimony" />
                </div>

                <div className="login-showcase__copy">
                    <span className="login-eyebrow"><Sparkles size={14} /> Matrimony management</span>
                    <h1>Bringing hearts together,<br />one match at a time.</h1>
                    <p>Manage trusted profiles, meaningful matches and wedding services from one secure place.</p>
                </div>

                <div className="login-showcase__trust">
                    <ShieldCheck size={17} />
                    <span><strong>Secure admin portal</strong> · Authorized access only</span>
                </div>
            </section>

            <section className="login-panel">
                <div className="login-form-shell">
                    <div className="login-mobile-brand">
                        <img className="login-brand__logo" src="/logo.png" alt="Gettimelam Matrimony" />
                    </div>

                    <header className="login-header">
                        <span className="login-panel__badge"><ShieldCheck size={15} /> Admin console</span>
                        <h2>Welcome back</h2>
                        <p>Enter your credentials to continue to the dashboard.</p>
                    </header>

                    <form className="login-form" onSubmit={handleSubmit}>
                        <label className="login-field">
                            <span className="login-field__label">Admin email</span>
                            <span className={`login-input-wrap ${emailError ? 'is-invalid' : ''}`}>
                                <Mail size={19} aria-hidden="true" />
                                <input
                                    type="email"
                                    autoComplete="email"
                                    placeholder="Enter admin email"
                                    value={form.email}
                                    onChange={updateEmail}
                                    onBlur={() => { if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) setEmailError('Enter a valid admin email address.'); }}
                                    aria-invalid={Boolean(emailError)}
                                    aria-describedby={emailError ? 'admin-email-error' : undefined}
                                    required
                                />
                            </span>
                            {emailError && <small id="admin-email-error" className="login-field__error">{emailError}</small>}
                        </label>

                        <label className="login-field">
                            <span className="login-field__label">Password</span>
                            <span className="login-input-wrap">
                                <LockKeyhole size={19} aria-hidden="true" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    placeholder="Enter your password"
                                    value={form.password}
                                    onChange={updateField('password')}
                                    required
                                />
                                <button
                                    className="login-password-toggle"
                                    type="button"
                                    onClick={() => setShowPassword(value => !value)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                                </button>
                            </span>
                        </label>

                        <button className="login-submit" type="submit" disabled={loading}>
                            <span>{loading ? 'Signing in…' : 'Sign in to dashboard'}</span>
                            {!loading && <ArrowRight size={19} />}
                            {loading && <span className="login-spinner" aria-hidden="true" />}
                        </button>
                    </form>

                    <div className="login-security-note">
                        <ShieldCheck size={18} />
                        <p><strong>Protected access</strong><br />Your session is encrypted and securely monitored.</p>
                    </div>
                </div>

                <footer className="login-footer">
                    © {new Date().getFullYear()} Gettimelam Matrimony. All rights reserved.
                </footer>
            </section>
        </main>
    );
};

export default Login;
